import { useLearningUiTranslation } from '@features/learning/shared/use-learning-ui-translation'
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { buildLearningEbookLessonsPath } from '@app/routes/route-paths'
import { ArrowLeft, CheckCircle, Presentation } from 'lucide-react'
import {
  markLessonAsRead,
} from '@services/firebase/learning'
import { useDashboardState } from '@features/dashboard'
import { LearnerAccessRequiredPanel } from '@features/premium/components/LearnerAccessRequiredPanel'
import { deriveLearnerAccessStatus } from '@features/premium/shared/learner-access-status'
import { OnlineBookContentRenderer } from '@features/learning/shared/OnlineBookContentRenderer'
import {
  getOnlineBookDetailsQueryOptions,
  prefetchOnlineBookDetails,
} from '@features/learning/shared/online-book-detail-query'
import {
  AppEmptyState,
  AppErrorState,
  AppLoadingState,
  LmsButton,
} from '@shared/ui'
import {
  clearLocalLessonProgress,
  formatLessonTitle,
  isLessonCompleted,
  resolveBookTitle,
  resolveReaderLanguage,
  upsertLocalLessonProgress,
} from '@features/learning/shared/learning-helpers'
import { useSnackbar } from '@shared/ui/notification-context'
import { useTranslation } from 'react-i18next'
import { logLearningPerformanceTrace } from '@shared/learning/learning-performance-trace'
import { useStudyTimeTracker } from '@features/learning/shared/useStudyTimeTracker'
import { loadPdfFlipbookReaderModule } from './pdf-flipbook-reader-loader'
import '../../../legacy-compat/pages/dashboard/learning-books/legacy-online-books.css'
import './online-book-reader.css'

const PdfFlipbookReader = lazy(loadPdfFlipbookReaderModule)

const TeachingPresentationScreen = lazy(() =>
  import('@features/learning/presentation/TeachingPresentationScreen').then((module) => ({
    default: module.TeachingPresentationScreen,
  })),
)

function getDocumentScrollProgress() {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return 0
  }

  const scrollTop = window.pageYOffset
  const documentHeight = Math.max(
    document.body.scrollHeight,
    document.documentElement.scrollHeight,
    document.body.offsetHeight,
    document.documentElement.offsetHeight,
    document.body.clientHeight,
    document.documentElement.clientHeight,
  )
  const totalScrollable = documentHeight - window.innerHeight

  if (totalScrollable <= 0) {
    return 100
  }

  return Math.max(0, Math.min(100, Math.floor((scrollTop / totalScrollable) * 100)))
}

function getPresentationWindowState() {
  if (typeof window === 'undefined') {
    return { isProjectorView: false, sessionId: undefined }
  }

  const searchParams = new URLSearchParams(window.location.search)

  return {
    isProjectorView: searchParams.get('presentationView') === 'projector',
    lessonId: searchParams.get('presentationLessonId') ?? undefined,
    sessionId: searchParams.get('presentationSession') ?? undefined,
  }
}

function getPdfReaderSlot(): 'bookPdf' | 'workbookPdf' | null {
  if (typeof window === 'undefined') {
    return null
  }

  const slot = new URLSearchParams(window.location.search).get('pdfSlot')
  return slot === 'bookPdf' || slot === 'workbookPdf' ? slot : null
}

function ReaderTopHeader({
  onBack,
  onOpenPresentation,
  title,
}: {
  readonly onBack: () => void
  readonly onOpenPresentation: () => void
  readonly title: string
}) {
  const ui = useLearningUiTranslation()
  return (
    <header className="onlineBookReaderHeader">
      <div className="onlineBookReaderHeaderInner">
        <LmsButton
          aria-label={ui("Back")}
          className="size-11"
          onClick={onBack}
          size="icon"
          variant="ghost"
        >
          <ArrowLeft aria-hidden="true" className="size-5" />
        </LmsButton>
        <h1 className="onlineBookReaderTitle">{title}</h1>
        <LmsButton
          aria-label={ui("Open teaching presentation")}
          onClick={onOpenPresentation}
          size="icon"
          variant="ghost"
        >
          <Presentation aria-hidden="true" className="size-5" />
        </LmsButton>
      </div>
    </header>
  )
}

export function OnlineBookReaderScreen() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()
  const { t, i18n } = useTranslation('book')
  const { t: tMembership } = useTranslation('translation')
  const { actions, state } = useDashboardState()
  const [isPresentationOpen, setIsPresentationOpen] = useState(false)
  const [isSavingReadState, setIsSavingReadState] = useState(false)
  const reportedReaderCommitRef = useRef<string | null>(null)
  const accessStatus = deriveLearnerAccessStatus({ locale: i18n.language, profile: state.myUser, t: tMembership })

  const { isProjectorView, lessonId: projectorLessonId, sessionId: projectorSessionId } = getPresentationWindowState()
  const pdfReaderSlot = getPdfReaderSlot()
  const isPdfReader = Boolean(pdfReaderSlot)
  const activeLesson = isProjectorView
    ? state.lessons.find((lesson) => lesson.id === projectorLessonId) ?? state.lesson
    : state.lesson
  const isCompleted = isLessonCompleted(activeLesson?.id ?? '', state.myUser)
  const title = activeLesson
    ? formatLessonTitle(t, activeLesson, i18n.language)
    : t('Online Ebook')
  const activeLessonId = activeLesson?.id ?? ''
  const activeBookId = activeLesson?.bookId ?? state.book?.id ?? null
  useStudyTimeTracker({
    enabled:
      Boolean(state.lesson) &&
      accessStatus.hasActiveAccess &&
      !isProjectorView &&
      !isPdfReader &&
      !isPresentationOpen,
    surface: 'online-ebook',
  })
  const lessonDetailsQuery = useQuery({
    ...getOnlineBookDetailsQueryOptions({
      bookId: activeBookId,
      lessonId: activeLessonId,
    }),
    enabled: Boolean(activeLessonId) && !isPdfReader && accessStatus.hasActiveAccess,
  })
  const blocks = lessonDetailsQuery.data ?? []
  const isLoading = Boolean(
    activeLessonId &&
      !isPdfReader &&
      (lessonDetailsQuery.isPending ||
        (lessonDetailsQuery.isFetching && !lessonDetailsQuery.data)),
  )
  const errorMessage = lessonDetailsQuery.error
    ? lessonDetailsQuery.error instanceof Error
      ? lessonDetailsQuery.error.message
      : t('Failed to load the online lesson content.')
    : null
  const adjacentLessonIds = useMemo(() => {
    if (!activeLessonId) {
      return []
    }

    const activeIndex = state.lessons.findIndex((lesson) => lesson.id === activeLessonId)

    if (activeIndex < 0) {
      return []
    }

    return [
      state.lessons[activeIndex - 1]?.id,
      state.lessons[activeIndex + 1]?.id,
    ].filter((lessonId): lessonId is string => Boolean(lessonId))
  }, [activeLessonId, state.lessons])

  useEffect(() => {
    if (!activeLessonId || isPdfReader || !accessStatus.hasActiveAccess) {
      return
    }

    adjacentLessonIds.forEach((lessonId) => {
      void prefetchOnlineBookDetails({
        bookId: activeBookId,
        lessonId,
        queryClient,
      }).catch(() => undefined)
    })
  }, [
    accessStatus.hasActiveAccess,
    activeBookId,
    activeLessonId,
    adjacentLessonIds,
    isPdfReader,
    queryClient,
  ])

  useEffect(() => {
    if (
      !activeLessonId ||
      isPdfReader ||
      !accessStatus.hasActiveAccess ||
      isLoading ||
      errorMessage ||
      !lessonDetailsQuery.data
    ) {
      return
    }

    const commitKey = `${activeBookId ?? 'unknown-book'}:${activeLessonId}:${lessonDetailsQuery.dataUpdatedAt}`

    if (reportedReaderCommitRef.current === commitKey) {
      return
    }

    const frame = window.requestAnimationFrame(() => {
      reportedReaderCommitRef.current = commitKey
      logLearningPerformanceTrace('reader.onlineBookDetails.renderCommit', {
        blocks: blocks.length,
        bookId: activeBookId,
        lessonId: activeLessonId,
        surface: isProjectorView ? 'projector' : 'modern-reader',
      })
    })

    return () => {
      window.cancelAnimationFrame(frame)
    }
  }, [
    activeBookId,
    activeLessonId,
    accessStatus.hasActiveAccess,
    blocks.length,
    errorMessage,
    isLoading,
    isPdfReader,
    isProjectorView,
    lessonDetailsQuery.data,
    lessonDetailsQuery.dataUpdatedAt,
  ])

  useEffect(() => {
    const lessonId = activeLesson?.id

    if (
      !lessonId ||
      isCompleted ||
      isPresentationOpen ||
      isProjectorView ||
      isPdfReader ||
      !accessStatus.hasActiveAccess
    ) {
      return
    }

    const handleScroll = () => {
      window.requestAnimationFrame(() => {
        upsertLocalLessonProgress(lessonId, getDocumentScrollProgress())
      })
    }

    document.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      document.removeEventListener('scroll', handleScroll)
    }
  }, [
    accessStatus.hasActiveAccess,
    activeLesson?.id,
    isCompleted,
    isPdfReader,
    isPresentationOpen,
    isProjectorView,
  ])

  const handleConfirmRead = async () => {
    if (!state.lesson?.id || !accessStatus.hasActiveAccess) {
      return
    }

    try {
      setIsSavingReadState(true)
      const progressLessons = await markLessonAsRead({
        bookId: state.lesson.bookId ?? state.book?.id,
        lessonId: state.lesson.id,
      })
      actions.mergeMyUser({ progress_lessons: progressLessons })
      clearLocalLessonProgress(state.lesson.id)
      enqueueSnackbar(i18n.t('Updated successfully', { ns: 'translation' }), {
        variant: 'success',
      })
    } catch {
      enqueueSnackbar(i18n.t('Updated failed', { ns: 'translation' }), {
        variant: 'error',
      })
    } finally {
      setIsSavingReadState(false)
    }
  }

  if (isProjectorView) {
    if (!accessStatus.hasActiveAccess) {
      return (
        <main className="onlineBookReaderMain">
          <LearnerAccessRequiredPanel
            accessStatus={accessStatus}
            contextLabel={title}
            onBuyKey={() => {
              actions.setSelectPremiumAccountPayment(true)
            }}
            onEnterKey={() => {
              actions.setEnterKeyPremium(true)
            }}
          />
        </main>
      )
    }

    if (!activeLesson || isLoading) {
      return <AppLoadingState message={t('Loading teaching presentation')} />
    }

    if (errorMessage) {
      return <AppErrorState message={errorMessage} />
    }

    return (
      <Suspense fallback={<AppLoadingState message={t('Loading teaching presentation')} />}>
        <TeachingPresentationScreen
          blocks={blocks}
          lessonId={activeLesson.id}
          lessonTitle={title}
          locale={resolveReaderLanguage(i18n.language)}
          mode="projector"
          onExit={() => {
            window.close()
          }}
          sessionId={projectorSessionId}
        />
      </Suspense>
    )
  }

  if (pdfReaderSlot) {
    if (!accessStatus.hasActiveAccess) {
      return (
        <>
          <ReaderTopHeader
            onBack={() => {
              navigate(
                buildLearningEbookLessonsPath(state.book?.id, {
                  preserveCurrentSearch: true,
                }),
              )
            }}
            onOpenPresentation={() => {
              setIsPresentationOpen(true)
            }}
            title={title}
          />
          <main className="onlineBookReaderMain">
            <LearnerAccessRequiredPanel
              accessStatus={accessStatus}
              contextLabel={resolveBookTitle(state.book, i18n.language, t('Online Ebook')) ?? t('Online Ebook')}
              onBuyKey={() => {
                actions.setSelectPremiumAccountPayment(true)
              }}
              onEnterKey={() => {
                actions.setEnterKeyPremium(true)
              }}
            />
          </main>
        </>
      )
    }

    return (
      <Suspense fallback={<AppLoadingState message={t('Loading online-book content')} />}>
        <PdfFlipbookReader
          bookId={state.book?.id ?? null}
          bookTitle={resolveBookTitle(state.book, i18n.language, t('Online Ebook')) ?? t('Online Ebook')}
          onBack={() => {
            navigate(
              buildLearningEbookLessonsPath(state.book?.id, {
                preserveCurrentSearch: true,
              }),
            )
          }}
          slot={pdfReaderSlot}
        />
      </Suspense>
    )
  }

  if (isPresentationOpen && state.lesson) {
    if (!accessStatus.hasActiveAccess) {
      return (
        <main className="onlineBookReaderMain">
          <LearnerAccessRequiredPanel
            accessStatus={accessStatus}
            contextLabel={title}
            onBuyKey={() => {
              actions.setSelectPremiumAccountPayment(true)
            }}
            onEnterKey={() => {
              actions.setEnterKeyPremium(true)
            }}
          />
        </main>
      )
    }

    return (
      <Suspense fallback={<AppLoadingState message={t('Loading teaching presentation')} />}>
        <TeachingPresentationScreen
          blocks={blocks}
          lessonId={state.lesson.id}
          lessonTitle={title}
          locale={resolveReaderLanguage(i18n.language)}
          onExit={() => {
            setIsPresentationOpen(false)
          }}
        />
      </Suspense>
    )
  }

  return (
    <>
      <ReaderTopHeader
        onBack={() => {
          navigate(
            buildLearningEbookLessonsPath(state.book?.id, {
              preserveCurrentSearch: true,
            }),
          )
        }}
        onOpenPresentation={() => {
          setIsPresentationOpen(true)
        }}
        title={title}
      />
      <main className={`onlineBookReaderMain${state.lesson ? ' hasLesson' : ''}`}>
        {!state.lesson ? (
          <AppEmptyState message={t('Select a lesson first to open its online-book content.')} />
        ) : null}
        {state.lesson && !accessStatus.hasActiveAccess ? (
          <LearnerAccessRequiredPanel
            accessStatus={accessStatus}
            contextLabel={title}
            onBuyKey={() => {
              actions.setSelectPremiumAccountPayment(true)
            }}
            onEnterKey={() => {
              actions.setEnterKeyPremium(true)
            }}
          />
        ) : null}
        {state.lesson && accessStatus.hasActiveAccess && isLoading ? (
          <AppLoadingState message={t('Loading online-book content')} />
        ) : null}
        {state.lesson && accessStatus.hasActiveAccess && !isLoading && errorMessage ? (
          <AppErrorState message={errorMessage} />
        ) : null}
        {state.lesson && accessStatus.hasActiveAccess && !isLoading && !errorMessage && blocks.length === 0 ? (
          <AppEmptyState message={t('This lesson does not have published online-book blocks yet.')} />
        ) : null}
        {state.lesson && accessStatus.hasActiveAccess && !isLoading && !errorMessage && blocks.length > 0 ? (
          <OnlineBookContentRenderer
            blocks={blocks}
            locale={resolveReaderLanguage(i18n.language)}
          />
        ) : null}
      </main>
      {state.lesson && !isPresentationOpen && accessStatus.hasActiveAccess ? (
        <footer className="onlineBookReaderFooter">
          <div className="onlineBookReaderFooterInner">
            <LmsButton
              className="onlineBookReaderConfirmButton"
              disabled={isCompleted || isSavingReadState}
              isLoading={isSavingReadState}
              loadingLabel={t('Saving')}
              mobileFullWidth
              onClick={() => {
                void handleConfirmRead()
              }}
              variant={isCompleted ? 'success' : 'accent'}
            >
              {isCompleted ? (
                <>
                  <CheckCircle aria-hidden="true" className="size-4" />
                  <span>{t('Readed')}</span>
                </>
              ) : (
                t('Confirm readed')
              )}
            </LmsButton>
          </div>
        </footer>
      ) : null}
    </>
  )
}
